import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { Btn, Input, Textarea, ImageField, Field, labelCls, inputCls, ListEditor, Toggle } from "./AdminUI";
import { Plus, X, Save, CheckCircle2, ChevronUp, ChevronDown } from "lucide-react";
import { ACTION_TYPES } from "../../components/StickyActions";

const DEFAULT_FABS = [
  { enabled: true, type: "whatsapp", position: "right", order: 0 },
  { enabled: true, type: "phone", position: "right", order: 1 },
];

function FabList({ title, subtitle, items, onChange }) {
  const list = items || [];
  const TYPE_KEYS = Object.keys(ACTION_TYPES);
  const add = () => onChange([...list, { enabled: true, type: "whatsapp", url: "", color: "", icon_color: "#ffffff", order: list.length, position: "right", new_tab: true }]);
  const upd = (i, f, v) => { const a = [...list]; a[i] = { ...a[i], [f]: v }; onChange(a); };
  const del = (i) => onChange(list.filter((_, k) => k !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const a = [...list];[a[i], a[j]] = [a[j], a[i]]; a.forEach((x, k) => (x.order = k)); onChange(a); };
  const cls = "w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-gold";
  return (
    <div className="rounded-xl2 bg-white border border-line p-5" data-testid={`fab-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink">{title} Buttons</h3>
        <Btn variant="outline" onClick={add} data-testid={`add-fab-${title.toLowerCase()}`}><Plus size={14} /> Add</Btn>
      </div>
      <p className="text-xs text-muted mb-3">{subtitle}</p>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-muted">No buttons — nothing will render on {title.toLowerCase()}.</p>}
        {list.map((a, i) => (
          <div key={i} className="rounded-lg border border-line p-3 bg-sand space-y-2" data-testid={`fab-${title.toLowerCase()}-row-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Toggle label="On" checked={a.enabled !== false} onChange={(v) => upd(i, "enabled", v)} />
              <select className={cls + " max-w-[150px]"} value={a.type || "custom"} onChange={(e) => upd(i, "type", e.target.value)} data-testid={`fab-${title.toLowerCase()}-type-${i}`}>
                {TYPE_KEYS.map((t) => <option key={t} value={t}>{ACTION_TYPES[t].label}</option>)}
              </select>
              <select className={cls + " max-w-[140px]"} value={a.position || "right"} onChange={(e) => upd(i, "position", e.target.value)}><option value="right">Bottom Right</option><option value="left">Bottom Left</option></select>
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={a.new_tab !== false} onChange={(e) => upd(i, "new_tab", e.target.checked)} className="accent-[#C88A32]" /> New tab</label>
              <button onClick={() => move(i, -1)} className="text-muted p-1"><ChevronUp size={16} /></button>
              <button onClick={() => move(i, 1)} className="text-muted p-1"><ChevronDown size={16} /></button>
              <button onClick={() => del(i)} className="text-red-500 p-1 ml-auto" data-testid={`fab-${title.toLowerCase()}-del-${i}`}><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-3 gap-2">
              <input className={cls} placeholder={a.type === "phone" || a.type === "whatsapp" ? "Number (blank = site phone)" : a.type === "email" ? "Email (blank = site email)" : "URL / link"} value={a.url || ""} onChange={(e) => upd(i, "url", e.target.value)} />
              <label className="flex items-center gap-2 text-xs text-muted">Button<input type="color" value={a.color || ACTION_TYPES[a.type || "custom"].color} onChange={(e) => upd(i, "color", e.target.value)} className="w-9 h-8 rounded border border-line" /></label>
              <label className="flex items-center gap-2 text-xs text-muted">Icon<input type="color" value={a.icon_color || "#ffffff"} onChange={(e) => upd(i, "icon_color", e.target.value)} className="w-9 h-8 rounded border border-line" /></label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ObjectList({ label, items, onChange, fields }) {
  const add = () => onChange([...(items || []), Object.fromEntries(fields.map((f) => [f, ""]))]);
  const upd = (i, f, v) => { const a = [...items]; a[i] = { ...a[i], [f]: v }; onChange(a); };
  const rm = (i) => onChange(items.filter((_, k) => k !== i));
  return (
    <Field label={label}>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2 items-start rounded-lg border border-line p-2 bg-sand">
            <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, minmax(0,1fr))` }}>
              {fields.map((f) => <input key={f} className={inputCls} placeholder={f} value={it[f] ?? ""} onChange={(e) => upd(i, f, e.target.value)} />)}
            </div>
            <button type="button" onClick={() => rm(i)} className="text-red-500 p-2"><X size={16} /></button>
          </div>
        ))}
        <Btn variant="outline" onClick={add}><Plus size={14} /> Add</Btn>
      </div>
    </Field>
  );
}

export default function SettingsAdmin() {
  const { reload } = useSettings();
  const [tab, setTab] = useState("company");
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {}));
    api.get("/content").then((r) => setContent(r.data || {}));
  }, []);

  const saveSettings = async () => { await api.put("/settings", settings); reload(); flash("Company information saved"); };
  const saveContent = async () => { await api.put("/content", content); reload(); flash("Website content saved"); };
  const flash = (m) => { setSaved(m); setTimeout(() => setSaved(""), 2500); };

  const sF = (k, v) => setSettings({ ...settings, [k]: v });
  const cF = (k, v) => setContent({ ...content, [k]: v });

  if (!settings || !content) return <p className="text-muted">Loading...</p>;

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-ink mb-4">Settings & Website Content</h1>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("company")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "company" ? "bg-ink text-white" : "border border-line"}`}>Company Information</button>
        <button onClick={() => setTab("content")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "content" ? "bg-ink text-white" : "border border-line"}`}>Website Content</button>
        <button onClick={() => setTab("sticky")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "sticky" ? "bg-ink text-white" : "border border-line"}`}>Sticky Actions</button>
      </div>

      {saved && <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2"><CheckCircle2 size={16} /> {saved}</div>}

      {tab === "company" && (
        <div className="rounded-xl2 bg-white border border-line p-6 space-y-4" data-testid="settings-company">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Company Name" value={settings.company_name} onChange={(v) => sF("company_name", v)} />
            <Input label="Tagline" value={settings.tagline} onChange={(v) => sF("tagline", v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <ImageField label="Logo (full wordmark)" value={settings.logo_url} onChange={(v) => sF("logo_url", v)} folder="gnons/settings/" />
            <ImageField label="Favicon" value={settings.favicon_url} onChange={(v) => sF("favicon_url", v)} folder="gnons/settings/" />
          </div>
          <div className="rounded-lg border border-line p-4 bg-sand space-y-4" data-testid="brand-settings">
            <h3 className="font-bold text-ink text-sm">Header Brand Lockup</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <ImageField label="Header Logo Icon (monogram)" value={settings.logo_icon_url} onChange={(v) => sF("logo_icon_url", v)} folder="gnons/settings/" />
              <Input label="Brand Text (beside logo)" value={settings.brand_text} onChange={(v) => sF("brand_text", v)} placeholder="GNONS" />
            </div>
            <Toggle label="Show brand text beside logo" checked={settings.show_brand_text !== false} onChange={(v) => sF("show_brand_text", v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Phone" value={settings.phone} onChange={(v) => sF("phone", v)} />
            <Input label="WhatsApp (digits only, with country code)" value={settings.whatsapp} onChange={(v) => sF("whatsapp", v)} />
            <Input label="Email" value={settings.email} onChange={(v) => sF("email", v)} />
            <Input label="Website" value={settings.website} onChange={(v) => sF("website", v)} />
          </div>
          <Textarea label="Office Address" value={settings.address} onChange={(v) => sF("address", v)} />
          <Input label="Google Maps URL" value={settings.google_maps_url} onChange={(v) => sF("google_maps_url", v)} />
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Instagram" value={settings.instagram} onChange={(v) => sF("instagram", v)} />
            <Input label="Facebook" value={settings.facebook} onChange={(v) => sF("facebook", v)} />
            <Input label="YouTube" value={settings.youtube} onChange={(v) => sF("youtube", v)} />
            <Input label="LinkedIn" value={settings.linkedin} onChange={(v) => sF("linkedin", v)} />
          </div>
          <Input label="Business Hours" value={settings.business_hours} onChange={(v) => sF("business_hours", v)} />
          <Input label="Employee CRM URL (for Employee Login button)" value={settings.crm_url} onChange={(v) => sF("crm_url", v)} placeholder="https://crm.gnons.in" />
          <div className="flex justify-end"><Btn onClick={saveSettings} data-testid="save-settings-btn"><Save size={15} /> Save Company Info</Btn></div>
        </div>
      )}

      {tab === "content" && (
        <div className="rounded-xl2 bg-white border border-line p-6 space-y-4" data-testid="settings-content">
          <h3 className="font-bold text-ink">Hero Section</h3>
          <Input label="Hero Kicker" value={content.hero_kicker} onChange={(v) => cF("hero_kicker", v)} />
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Heading Line 1" value={content.hero_heading_1} onChange={(v) => cF("hero_heading_1", v)} />
            <Input label="Heading Line 2 (gold)" value={content.hero_heading_2} onChange={(v) => cF("hero_heading_2", v)} />
            <Input label="Heading Line 3" value={content.hero_heading_3} onChange={(v) => cF("hero_heading_3", v)} />
          </div>
          <Textarea label="Hero Subheading" value={content.hero_subheading} onChange={(v) => cF("hero_subheading", v)} />
          <ImageField label="Hero Image" value={content.hero_image} onChange={(v) => cF("hero_image", v)} folder="gnons/settings/" />
          <ObjectList label="Statistics" items={content.stats} onChange={(v) => cF("stats", v)} fields={["value", "label", "icon"]} />

          <hr className="border-line" />
          <h3 className="font-bold text-ink">About</h3>
          <Textarea label="About Intro" value={content.about_intro} onChange={(v) => cF("about_intro", v)} rows={4} />
          <Textarea label="Mission" value={content.mission} onChange={(v) => cF("mission", v)} />
          <Textarea label="Vision" value={content.vision} onChange={(v) => cF("vision", v)} />
          <ListEditor label="Core Values" items={content.core_values} onChange={(v) => cF("core_values", v)} />
          <ObjectList label="Why Choose GNONS" items={content.why_choose} onChange={(v) => cF("why_choose", v)} fields={["title", "desc", "icon"]} />
          <ObjectList label="Working Process" items={content.process} onChange={(v) => cF("process", v)} fields={["step", "title", "desc", "icon"]} />
          <ObjectList label="FAQs" items={content.faqs} onChange={(v) => cF("faqs", v)} fields={["q", "a"]} />

          <hr className="border-line" />
          <h3 className="font-bold text-ink">Smart Enquiry Popup</h3>
          {(() => {
            const pop = content.popup || {};
            const pF = (k, v) => cF("popup", { ...pop, [k]: v });
            return (
              <div className="space-y-4" data-testid="popup-settings">
                <Toggle label="Enable Popup" checked={!!pop.enabled} onChange={(v) => pF("enabled", v)} />
                <Input label="Heading" value={pop.heading} onChange={(v) => pF("heading", v)} />
                <Textarea label="Supporting Text" value={pop.text} onChange={(v) => pF("text", v)} />
                <Input label="CTA Label" value={pop.cta} onChange={(v) => pF("cta", v)} />
                <ImageField label="Popup Image" value={pop.image} onChange={(v) => pF("image", v)} folder="gnons/settings/" />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Desktop Delay (sec)" type="number" value={pop.desktop_delay} onChange={(v) => pF("desktop_delay", parseInt(v) || 0)} />
                  <Input label="Mobile Delay (sec)" type="number" value={pop.mobile_delay} onChange={(v) => pF("mobile_delay", parseInt(v) || 0)} />
                  <Input label="Desktop Scroll %" type="number" value={pop.desktop_scroll} onChange={(v) => pF("desktop_scroll", parseInt(v) || 0)} />
                  <Input label="Mobile Scroll %" type="number" value={pop.mobile_scroll} onChange={(v) => pF("mobile_scroll", parseInt(v) || 0)} />
                </div>
                <Toggle label="Desktop Exit Intent" checked={!!pop.exit_intent} onChange={(v) => pF("exit_intent", v)} />
              </div>
            );
          })()}

          <div className="flex justify-end"><Btn onClick={saveContent} data-testid="save-content-btn"><Save size={15} /> Save Website Content</Btn></div>
        </div>
      )}
      {tab === "sticky" && (
        <div className="space-y-5" data-testid="settings-sticky">
          <p className="text-sm text-muted">Floating buttons configured independently for Mobile and Desktop. Empty list = nothing renders on that device. The bottom Call/WhatsApp bar is a separate system and is unaffected.</p>
          <FabList title="Mobile" subtitle="Shown only on phones/tablets (screens < 768px)." items={settings.sticky_mobile ?? DEFAULT_FABS} onChange={(v) => sF("sticky_mobile", v)} />
          <FabList title="Desktop" subtitle="Shown only on desktop/laptop (screens ≥ 768px)." items={settings.sticky_desktop ?? DEFAULT_FABS} onChange={(v) => sF("sticky_desktop", v)} />
          <Btn onClick={saveSettings} data-testid="save-sticky"><Save size={16} /> Save Floating Buttons</Btn>
        </div>
      )}
    </div>
  );
}
